import { SignInPrompt } from '@/features/account/SignInPrompt';
import { ErrorState, LoadingState } from '@/components/ui/async-state';
import { Icon } from '@/components/ui/icon';
import { AppScreen, IconAction } from '@/components/ui/scene';
import { Switch } from '@/components/ui/switch';
import { Text } from '@/components/ui/text';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import type { EmailPreferences, UpdateEmailPreferencesRequest } from '@/lib/api/apiTypes';
import { useAuth } from '@/lib/auth/auth';
import {
  getCurrentExpoPushToken,
  getExpoPushToken,
  getNotificationPermissionStatus,
  getPushCapabilityStatus,
  type PushCapabilityStatus,
  requestNotificationPermission,
} from '@/lib/notifications/notifications';
import { trackNotificationPermission } from '@/lib/analytics/posthog';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { useEmailPreferences, useUpdateEmailPreferences } from '@/features/account/useEmailPreferences';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '@/features/account/useNotificationPreferences';
import {
  enablePushDeviceRegistration,
  type PushPermissionStatus,
} from '@/features/account/pushPreferenceFlow';
import { showToast } from '@/lib/useToastStore';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import { router, useLocalSearchParams } from 'expo-router';
import {
  ArrowLeftIcon,
  BellRingIcon,
  CalendarClockIcon,
  MailIcon,
  MegaphoneIcon,
  SparklesIcon,
  RadioIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Alert, Platform, Pressable, ScrollView, View } from 'react-native';

type NotificationSettingsCategory =
  | 'social'
  | 'reminders'
  | 'host_updates'
  | 'venue_rsvp'
  | 'weekly_digest';

function logPushSettingsDebug(message: string, details?: Record<string, unknown>) {
  if (!__DEV__) {
    return;
  }

  if (details) {
    console.log(`[push-settings] ${message}`, details);
    return;
  }

  console.log(`[push-settings] ${message}`);
}

export default function NotificationPreferencesScreen() {
  const params = useLocalSearchParams<{ category?: string }>();
  const session = useAuth((s) => s.session);
  const request = useAuth((s) => s.request);
  const isDesktop = useIsDesktop();
  const preferencesQuery = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  const emailPreferencesQuery = useEmailPreferences();
  const updateEmailPreferences = useUpdateEmailPreferences();
  const preferences = preferencesQuery.data?.preferences ?? null;
  const emailPreferences = emailPreferencesQuery.data ?? null;
  const showVenueRSVPToggle = Boolean(session?.me.profile.is_venue && session.me.profile.venue_id);
  const [permissionStatus, setPermissionStatus] = React.useState<PushPermissionStatus>('undetermined');
  const [pushCapability, setPushCapability] = React.useState<PushCapabilityStatus>({
    state: 'unknown',
    title: 'Checking',
    detail: 'Checking whether this device can receive Radious push alerts.',
  });
  const scrollViewRef = React.useRef<ScrollView>(null);
  const pushCategoryCardYRef = React.useRef(0);
  const pushCategoryOffsetsRef = React.useRef<Partial<Record<NotificationSettingsCategory, number>>>(
    {}
  );
  const [highlightedCategory, setHighlightedCategory] =
    React.useState<NotificationSettingsCategory | null>(null);
  const effectiveEmailPreferences = React.useMemo(
    () => getEffectiveEmailPreferences(emailPreferences),
    [emailPreferences]
  );
  const requestedCategory = React.useMemo(
    () => parseNotificationSettingsCategory(params.category),
    [params.category]
  );

  React.useEffect(() => {
    getNotificationPermissionStatus()
      .then(setPermissionStatus)
      .catch(() => undefined);
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    getPushCapabilityStatus(permissionStatus)
      .then((status) => {
        if (!cancelled) {
          setPushCapability(status);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPushCapability({
            state: 'unknown',
            title: 'Checking',
            detail: 'We could not verify push delivery on this device yet.',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [permissionStatus]);

  React.useEffect(() => {
    if (!preferences || !requestedCategory) {
      return;
    }

    const scrollTimer = setTimeout(() => {
      const rowOffset = pushCategoryOffsetsRef.current[requestedCategory];
      if (rowOffset == null) {
        return;
      }
      scrollViewRef.current?.scrollTo({
        y: Math.max(0, pushCategoryCardYRef.current + rowOffset - 24),
        animated: true,
      });
      setHighlightedCategory(requestedCategory);
    }, 120);

    const highlightTimer = setTimeout(() => {
      setHighlightedCategory(null);
    }, 1800);

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(highlightTimer);
    };
  }, [preferences, requestedCategory]);

  const handlePushCategoryCardLayout = React.useCallback((event: { nativeEvent: { layout: { y: number } } }) => {
    pushCategoryCardYRef.current = event.nativeEvent.layout.y;
  }, []);

  const handlePushCategoryRowLayout = React.useCallback(
    (category: NotificationSettingsCategory, y: number) => {
      pushCategoryOffsetsRef.current[category] = y;
    },
    []
  );

  const handleTogglePush = React.useCallback(
    async (nextValue: boolean) => {
      logPushSettingsDebug('toggling push preference', { nextValue });

      if (!session) {
        logPushSettingsDebug('stopped push toggle flow because there is no session');
        return;
      }

      if (!nextValue) {
        await updatePreferences.mutateAsync({ push_enabled: false });
        const token = await getCurrentExpoPushToken();
        logPushSettingsDebug('loaded current token for push disable', {
          hasToken: Boolean(token),
        });
        if (!token) {
          setPermissionStatus(await getNotificationPermissionStatus().catch(() => permissionStatus));
          return;
        }
        await request('/me/push-token', {
          method: 'DELETE',
          body: JSON.stringify({ token }),
        })
          .then(() => {
            logPushSettingsDebug('deleted push token after disabling pushes');
          })
          .catch((error) => {
            logPushSettingsDebug('failed to delete push token after disabling pushes', {
              error: error instanceof Error ? error.message : String(error),
            });
          });
        return;
      }

      const result = await enablePushDeviceRegistration({
        getPermissionStatus: getNotificationPermissionStatus,
        requestPermission: requestNotificationPermission,
        getToken: async () => {
          const token = await getExpoPushToken();
          logPushSettingsDebug('loaded Expo token from settings screen', {
            hasToken: Boolean(token),
          });
          return token;
        },
        registerToken: async (token) => {
          await request('/me/push-token', {
            method: 'POST',
            body: JSON.stringify({ token }),
          });
          logPushSettingsDebug('registered push token from settings screen', {
            tokenPreview: token.slice(0, 18),
          });
        },
        trackPermission: (granted) => {
          trackNotificationPermission({
            granted,
            source: 'notification_settings',
          });
        },
      });
      setPermissionStatus(result.status);
      logPushSettingsDebug('requested notification permission from settings screen', {
        status: result.status,
      });

      if (!result.ok && result.reason === 'permission') {
        showToast({ message: 'Enable notifications in iPhone Settings to receive pushes.' });
        return;
      }
      if (!result.ok) {
        showToast({ message: 'Unable to register this device for notifications right now.' });
        return;
      }

      await updatePreferences.mutateAsync({ push_enabled: true });
    },
    [permissionStatus, request, session, updatePreferences]
  );

  const handleToggleGroup = React.useCallback(
    async (
      key:
        | 'social_enabled'
        | 'reminders_enabled'
        | 'host_updates_enabled'
        | 'venue_rsvp_enabled'
        | 'weekly_digest_enabled',
      value: boolean
    ) => {
      await updatePreferences.mutateAsync({ [key]: value });
    },
    [updatePreferences]
  );

  const handleToggleEmailPreference = React.useCallback(
    async (
      key:
        | 'email_reminders'
        | 'email_digest'
        | 'email_marketing'
        | 'email_new_events'
        | 'email_friend_activity'
        | 'email_venue_updates'
        | 'email_product_updates',
      value: boolean
    ) => {
      const payload: UpdateEmailPreferencesRequest = { [key]: value };
      if (emailPreferences?.email_unsubscribed_all && value) {
        payload.email_unsubscribed_all = false;
      }

      try {
        await updateEmailPreferences.mutateAsync(payload);
      } catch {
        showToast({ message: 'Unable to update email preferences right now.' });
      }
    },
    [emailPreferences?.email_unsubscribed_all, updateEmailPreferences]
  );

  const handleUnsubscribeAllEmails = React.useCallback(() => {
    Alert.alert(
      'Unsubscribe from all emails?',
      'This will stop all Radious emails including event reminders.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Unsubscribe',
          style: 'destructive',
          onPress: () => {
            updateEmailPreferences
              .mutateAsync({ email_unsubscribed_all: true })
              .catch(() => showToast({ message: 'Unable to update email preferences right now.' }));
          },
        },
      ]
    );
  }, [updateEmailPreferences]);

  const isLoadingPreferences = session && (preferencesQuery.isPending || emailPreferencesQuery.isPending);
  const preferencesError = preferencesQuery.error ?? emailPreferencesQuery.error;
  const isPreferencesError = preferencesQuery.isError || emailPreferencesQuery.isError;
  const appStatus = preferences ? getPushAppStatus(preferences.push_enabled) : null;
  const devicePermissionStatus = preferences ? getDevicePermissionStatus(permissionStatus) : null;
  const deliveryStatus =
    preferences ? getPushDeliveryStatus(preferences.push_enabled, permissionStatus, pushCapability) : null;
  const pushStatusRows = React.useMemo(() => {
    if (!preferences || !appStatus || !devicePermissionStatus || !deliveryStatus) {
      return [];
    }

    if (!preferences.push_enabled) {
      return [{ label: 'Alerts in Radious', status: appStatus }];
    }

    if (permissionStatus !== 'granted') {
      return [
        {
          label: Platform.OS === 'ios' ? 'Notifications on iPhone' : 'Notifications on this device',
          status: devicePermissionStatus,
        },
      ];
    }

    if (pushCapability.state !== 'supported') {
      return [{ label: 'Getting alerts here', status: deliveryStatus }];
    }

    return [];
  }, [appStatus, deliveryStatus, devicePermissionStatus, permissionStatus, preferences, pushCapability.state]);

  return (
    <AppScreen>
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={{ paddingBottom: isDesktop ? 48 : 120 }}
        showsVerticalScrollIndicator={false}>
        <View
          style={
            isDesktop
              ? { maxWidth: 760, width: '100%', alignSelf: 'center', paddingHorizontal: 32 }
              : undefined
          }>
          <View className={isDesktop ? 'pt-4' : 'px-4 pt-4'}>
            <View className="flex-row items-center justify-between">
              <IconAction icon={ArrowLeftIcon} onPress={() => router.back()} />
              <Text className="text-3xl font-extrabold tracking-tight">Notifications</Text>
              <View className="size-11" />
            </View>
          </View>

          {!session ? (
            <View className={isDesktop ? 'pt-8' : 'px-4 pt-8'}>
              <SignInPrompt title="Control your alerts." body="Sign in to manage notification preferences." />
            </View>
          ) : null}

          {isLoadingPreferences ? <LoadingState label="Loading preferences..." /> : null}

          {isPreferencesError ? (
            <ErrorState
              title="Couldn't load notification preferences."
              body={getAPIErrorRecoveryCopy(preferencesError, undefined, 'Please try again.')}
              actionLabel="Retry"
              onAction={() =>
                Promise.allSettled([
                  preferencesQuery.refetch(),
                  emailPreferencesQuery.refetch(),
                ]).catch(() => undefined)
              }
            />
          ) : null}

          {preferences && effectiveEmailPreferences ? (
            <View className={isDesktop ? 'pt-8' : 'px-4 pt-6'}>
              {pushStatusRows.length > 0 ? (
                <View
                  className="rounded-[24px] border p-5"
                  style={{
                    borderColor: DESIGN_TOKENS.colors.borderStrong,
                    backgroundColor: DESIGN_TOKENS.colors.surface,
                  }}>
                  <View className="flex-row items-start gap-4">
                    <View
                      className="size-12 items-center justify-center rounded-2xl"
                      style={{ backgroundColor: DESIGN_TOKENS.colors.accentSoft }}>
                      <Icon as={BellRingIcon} size={22} color={DESIGN_TOKENS.colors.accentStrong} />
                    </View>
                    <View className="flex-1 gap-2">
                      <Text className="text-2xl font-extrabold tracking-tight text-foreground">
                        Notifications need attention
                      </Text>
                      <Text variant="muted" className="leading-6">
                        This only shows up when something is stopping alerts from reaching you.
                      </Text>
                    </View>
                  </View>
                  <View className="mt-5 gap-3">
                    {pushStatusRows.map((row) => (
                      <NotificationStatusRow key={row.label} label={row.label} status={row.status} />
                    ))}
                  </View>
                </View>
              ) : null}

              <PreferencesSectionLabel className={pushStatusRows.length > 0 ? 'mt-8' : undefined} title="Push Categories" />

              <View
                className="mt-3 overflow-hidden rounded-[24px] border border-border bg-card"
                onLayout={handlePushCategoryCardLayout}>
                <NotificationToggleRow
                  icon={BellRingIcon}
                  title="Push Notifications"
                  description="Master switch for all Radious push notifications on this device."
                  value={preferences.push_enabled}
                  disabled={updatePreferences.isPending}
                  onValueChange={(value) => handleTogglePush(value).catch(() => undefined)}
                />
                <NotificationToggleRow
                  category="social"
                  icon={UsersIcon}
                  title="Social"
                  description="Follows, friend RSVPs, and event comments."
                  value={preferences.social_enabled}
                  disabled={!preferences.push_enabled || updatePreferences.isPending}
                  highlighted={highlightedCategory === 'social'}
                  onLayout={handlePushCategoryRowLayout}
                  onValueChange={(value) =>
                    handleToggleGroup('social_enabled', value).catch(() => undefined)
                  }
                />
                <NotificationToggleRow
                  category="reminders"
                  icon={CalendarClockIcon}
                  title="Reminders"
                  description="Saved-event and going-event reminders before start time."
                  value={preferences.reminders_enabled}
                  disabled={!preferences.push_enabled || updatePreferences.isPending}
                  highlighted={highlightedCategory === 'reminders'}
                  onLayout={handlePushCategoryRowLayout}
                  onValueChange={(value) =>
                    handleToggleGroup('reminders_enabled', value).catch(() => undefined)
                  }
                />
                <NotificationToggleRow
                  category="host_updates"
                  icon={RadioIcon}
                  title="Host Updates"
                  description="New events from hosts and venues you follow."
                  value={preferences.host_updates_enabled}
                  disabled={!preferences.push_enabled || updatePreferences.isPending}
                  highlighted={highlightedCategory === 'host_updates'}
                  onLayout={handlePushCategoryRowLayout}
                  onValueChange={(value) =>
                    handleToggleGroup('host_updates_enabled', value).catch(() => undefined)
                  }
                />
                {showVenueRSVPToggle ? (
                  <NotificationToggleRow
                    category="venue_rsvp"
                    icon={BellRingIcon}
                    title="People Going"
                    description="A push when someone marks themselves going to one of your venue's events."
                    value={preferences.venue_rsvp_enabled}
                    disabled={!preferences.push_enabled || updatePreferences.isPending}
                    highlighted={highlightedCategory === 'venue_rsvp'}
                    onLayout={handlePushCategoryRowLayout}
                    onValueChange={(value) =>
                      handleToggleGroup('venue_rsvp_enabled', value).catch(() => undefined)
                    }
                  />
                ) : null}
                <NotificationToggleRow
                  category="weekly_digest"
                  icon={BellRingIcon}
                  title="Weekly Digest"
                  description="A weekly roundup when the weekend starts taking shape."
                  value={preferences.weekly_digest_enabled}
                  disabled={!preferences.push_enabled || updatePreferences.isPending}
                  highlighted={highlightedCategory === 'weekly_digest'}
                  isLast
                  onLayout={handlePushCategoryRowLayout}
                  onValueChange={(value) =>
                    handleToggleGroup('weekly_digest_enabled', value).catch(() => undefined)
                  }
                />
              </View>
              {pushStatusRows.length > 0 ? (
                <Text variant="small" className="mt-3 px-1 leading-5 text-muted-foreground">
                  Your alert toggles below work once notifications are turned on here and allowed on your device.
                </Text>
              ) : null}

              <PreferencesSectionLabel className="mt-8" title="Email Preferences" />

              {emailPreferences?.email_unsubscribed_all ? (
                <View className="mt-4 rounded-[20px] border border-border bg-card px-5 py-4">
                  <Text variant="small" className="leading-5 text-muted-foreground">
                    You are unsubscribed from all Radious emails. Toggle any preference above to resubscribe.
                  </Text>
                </View>
              ) : null}

              <PreferencesSectionLabel className="mt-6" title="Reminders & Digests" />
              <View className="mt-3 overflow-hidden rounded-[24px] border border-border bg-card">
                <NotificationToggleRow
                  icon={CalendarClockIcon}
                  title="Event Reminders"
                  description="Get notified 2 hours before events you saved or RSVPd to"
                  value={effectiveEmailPreferences.email_reminders}
                  disabled={updateEmailPreferences.isPending}
                  onValueChange={(value) =>
                    handleToggleEmailPreference('email_reminders', value).catch(() => undefined)
                  }
                />
                <NotificationToggleRow
                  icon={MailIcon}
                  title="Weekly Digest"
                  description="Top SA events every Thursday morning"
                  value={effectiveEmailPreferences.email_digest}
                  disabled={updateEmailPreferences.isPending}
                  isLast
                  onValueChange={(value) =>
                    handleToggleEmailPreference('email_digest', value).catch(() => undefined)
                  }
                />
              </View>

              <PreferencesSectionLabel className="mt-6" title="Updates & Activity" />
              <View className="mt-3 overflow-hidden rounded-[24px] border border-border bg-card">
                <NotificationToggleRow
                  icon={SparklesIcon}
                  title="Radious Weekly Picks"
                  description="Our curated roundup of the best nights in SA"
                  value={effectiveEmailPreferences.email_marketing}
                  disabled={updateEmailPreferences.isPending}
                  onValueChange={(value) =>
                    handleToggleEmailPreference('email_marketing', value).catch(() => undefined)
                  }
                />
                <NotificationToggleRow
                  icon={RadioIcon}
                  title="New Events from Venues You Follow"
                  description="Know when your favorite spots post something new"
                  value={effectiveEmailPreferences.email_new_events}
                  disabled={updateEmailPreferences.isPending}
                  onValueChange={(value) =>
                    handleToggleEmailPreference('email_new_events', value).catch(() => undefined)
                  }
                />
                <NotificationToggleRow
                  icon={UsersIcon}
                  title="Friend Activity"
                  description="A weekly summary of what your friends are going to"
                  value={effectiveEmailPreferences.email_friend_activity}
                  disabled={updateEmailPreferences.isPending}
                  onValueChange={(value) =>
                    handleToggleEmailPreference('email_friend_activity', value).catch(() => undefined)
                  }
                />
                <NotificationToggleRow
                  icon={MegaphoneIcon}
                  title="Venue Updates"
                  description="Special announcements from venues you follow"
                  value={effectiveEmailPreferences.email_venue_updates}
                  disabled={updateEmailPreferences.isPending}
                  onValueChange={(value) =>
                    handleToggleEmailPreference('email_venue_updates', value).catch(() => undefined)
                  }
                />
                <NotificationToggleRow
                  icon={BellRingIcon}
                  title="Product Updates"
                  description="New Radious features and improvements"
                  value={effectiveEmailPreferences.email_product_updates}
                  disabled={updateEmailPreferences.isPending}
                  isLast
                  onValueChange={(value) =>
                    handleToggleEmailPreference('email_product_updates', value).catch(() => undefined)
                  }
                />
                <SettingsTextButton
                  label="Unsubscribe from all emails"
                  disabled={updateEmailPreferences.isPending}
                  onPress={handleUnsubscribeAllEmails}
                />
              </View>
            </View>
          ) : null}
        </View>
      </ScrollView>
    </AppScreen>
  );
}

function PreferencesSectionLabel({ className, title }: { className?: string; title: string }) {
  return (
    <View className={className}>
      <Text variant="small" className="px-1 uppercase tracking-[1.6px] text-muted-foreground">
        {title}
      </Text>
    </View>
  );
}

type SettingsStatusTone = 'good' | 'warning' | 'neutral';

type SettingsStatus = {
  title: string;
  detail: string;
  tone: SettingsStatusTone;
};

function NotificationStatusRow({
  label,
  status,
}: {
  label: string;
  status: SettingsStatus;
}) {
  return (
    <View className="rounded-[18px] border px-4 py-3" style={getNotificationStatusContainerStyle(status.tone)}>
      <View className="flex-row items-start justify-between gap-4">
        <View className="flex-1 gap-1">
          <Text variant="small" className="uppercase tracking-[1.4px] text-muted-foreground">
            {label}
          </Text>
          <Text className="font-semibold text-foreground">{status.title}</Text>
          <Text variant="small" className="leading-5 text-muted-foreground">
            {status.detail}
          </Text>
        </View>
        <View className="rounded-full border px-3 py-1" style={getNotificationStatusBadgeStyle(status.tone)}>
          <Text variant="small" className="font-semibold text-foreground">
            {status.title}
          </Text>
        </View>
      </View>
    </View>
  );
}

function NotificationToggleRow({
  category,
  highlighted = false,
  icon: IconGlyph,
  title,
  description,
  value,
  disabled,
  onLayout,
  onValueChange,
  isLast = false,
}: {
  category?: NotificationSettingsCategory;
  highlighted?: boolean;
  icon: LucideIcon;
  title: string;
  description: string;
  value: boolean;
  disabled: boolean;
  onLayout?: (category: NotificationSettingsCategory, y: number) => void;
  onValueChange: (value: boolean) => void;
  isLast?: boolean;
}) {
  return (
    <View
      className={isLast ? 'px-5 py-4' : 'border-b border-border px-5 py-4'}
      onLayout={(event) => {
        if (category) {
          onLayout?.(category, event.nativeEvent.layout.y);
        }
      }}
      style={highlighted ? { backgroundColor: DESIGN_TOKENS.colors.accentSoft } : undefined}>
      <View className="flex-row items-start gap-4">
        <View
          className="mt-0.5 size-10 items-center justify-center rounded-2xl"
          style={{ backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}>
          <Icon as={IconGlyph} size={18} color={DESIGN_TOKENS.colors.text} />
        </View>
        <View className="flex-1 gap-1 pr-4">
          <Text className="font-semibold text-foreground">{title}</Text>
          <Text variant="small" className="leading-5 text-muted-foreground">
            {description}
          </Text>
        </View>
        <Switch checked={value} disabled={disabled} onCheckedChange={onValueChange} />
      </View>
    </View>
  );
}

function parseNotificationSettingsCategory(
  value: string | string[] | undefined
): NotificationSettingsCategory | null {
  const category = Array.isArray(value) ? value[0] : value;
  switch (category) {
    case 'social':
    case 'reminders':
    case 'host_updates':
    case 'venue_rsvp':
    case 'weekly_digest':
      return category;
    default:
      return null;
  }
}

function SettingsTextButton({
  label,
  disabled,
  onPress,
}: {
  label: string;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      className="border-t border-border px-5 py-4"
      disabled={disabled}
      onPress={onPress}>
      <Text className="font-medium text-primary">{label}</Text>
    </Pressable>
  );
}

function getEffectiveEmailPreferences(preferences: EmailPreferences | null) {
  if (!preferences) {
    return null;
  }
  if (!preferences.email_unsubscribed_all) {
    return preferences;
  }

  return {
    ...preferences,
    email_reminders: false,
    email_digest: false,
    email_marketing: false,
    email_new_events: false,
    email_friend_activity: false,
    email_venue_updates: false,
    email_product_updates: false,
  };
}

function getPushAppStatus(pushEnabled: boolean): SettingsStatus {
  if (pushEnabled) {
    return {
      title: 'On',
      detail: 'Radious is ready to send the alert types you keep turned on below.',
      tone: 'good',
    };
  }

  return {
    title: 'Turned off',
    detail: 'Notifications are turned off in Radious right now, so no alerts will come through until you switch them back on.',
    tone: 'neutral',
  };
}

function getDevicePermissionStatus(status: PushPermissionStatus): SettingsStatus {
  switch (status) {
    case 'granted':
      return {
        title: 'Allowed',
        detail: 'Your device is allowed to show Radious notifications.',
        tone: 'good',
      };
    case 'denied':
      return {
        title: 'Turned off',
        detail: 'Your device settings are blocking notifications right now. Turn them on in Settings to get alerts.',
        tone: 'warning',
      };
    default:
      return {
        title: 'Not allowed yet',
        detail: 'You have not allowed notifications on this device yet.',
        tone: 'neutral',
      };
  }
}

function getPushDeliveryStatus(
  pushEnabled: boolean,
  permissionStatus: PushPermissionStatus,
  capability: PushCapabilityStatus
): SettingsStatus {
  if (!pushEnabled) {
    return {
      title: 'Not active',
      detail: 'Turn notifications back on in Radious when you want this device to start getting alerts again.',
      tone: 'neutral',
    };
  }

  if (permissionStatus !== 'granted') {
    return {
      title: 'Blocked',
      detail: 'Your alert settings are on in Radious, but your device still needs permission before anything can come through.',
      tone: 'warning',
    };
  }

  switch (capability.state) {
    case 'supported':
      return {
        title: capability.title,
        detail: capability.detail,
        tone: 'good',
      };
    case 'unsupported':
      return {
        title: capability.title,
        detail: capability.detail,
        tone: 'warning',
      };
    default:
      return {
        title: capability.title,
        detail: capability.detail,
        tone: 'neutral',
      };
  }
}

function getNotificationStatusContainerStyle(tone: SettingsStatusTone) {
  if (tone === 'good') {
    return {
      borderColor: DESIGN_TOKENS.colors.accentEdge,
      backgroundColor: DESIGN_TOKENS.colors.accentSoft,
    };
  }

  if (tone === 'warning') {
    return {
      borderColor: DESIGN_TOKENS.colors.borderStrong,
      backgroundColor: DESIGN_TOKENS.colors.surfaceRaised,
    };
  }

  return {
    borderColor: DESIGN_TOKENS.colors.border,
    backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
  };
}

function getNotificationStatusBadgeStyle(tone: SettingsStatusTone) {
  if (tone === 'good') {
    return {
      borderColor: DESIGN_TOKENS.colors.accentEdge,
      backgroundColor: DESIGN_TOKENS.colors.surface,
    };
  }

  if (tone === 'warning') {
    return {
      borderColor: DESIGN_TOKENS.colors.borderStrong,
      backgroundColor: DESIGN_TOKENS.colors.surface,
    };
  }

  return {
    borderColor: DESIGN_TOKENS.colors.border,
    backgroundColor: DESIGN_TOKENS.colors.surface,
  };
}
